import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'store';
import QuizTypes from 'types/QuizTypes';
// Utilities
import HttpClient from 'utilities/HttpClient';

const _url:string = '/ai';
const _http:HttpClient = new HttpClient();

const AiChatAsync = {
  generateQuestions: createAsyncThunk(
    'aiChat/Generate questions',
    async (data: { message: string; amountQuestions: number }, { rejectWithValue, getState }) => {
      const state = getState() as RootState;
      const { quiz } = state.quizzes;
      const isScoringQuiz = quiz && [QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type);

      const systemPrompt = `
        You are an AI that generates quiz questions based on factual information.

        GENERAL RULES:
        - Keep responses clear and concise
        - Prefer verified and well-known facts
        - Do NOT invent clearly false information

        FAIL-SAFE:
        If no reasonable data is available, return:
        {"topic":"No available data","questions":[]}

        OUTPUT RULES:
        - Return ONLY valid JSON
        - No text before or after JSON
        - No markdown
        - JSON must be parseable

        RESPONSE SCHEMA:
        {
          "topic": string,
          "questions": [
            {
              "title": string,
              "answers": [
                {
                  "title": string${isScoringQuiz ? ', "correct": boolean' : ''}
                }
              ]
            }
          ]
        }

        REQUIREMENTS:
        - "questions" MUST be an array of objects
        - Each question MUST have "answers"
        - "answers" MUST contain at least 2 items
        ${isScoringQuiz ? '- EXACTLY ONE answer must have "correct": true' : ''}

        DATA QUALITY:
        - Avoid placeholders like "?", "...", "unknown"
        - Answers should be complete and readable
        - Do not include obviously incorrect or broken data

        IMPORTANT:
        - If some questions are uncertain, you may still generate others
        - Try to generate the requested number of questions, but prioritize quality

        EXAMPLE OUTPUT:
        {
          "topic": "football",
          "questions": [
            {
              "title": "How many players are on the field per team?",
              "answers": [
                { "title": "11"${isScoringQuiz ? ', "correct": true' : ''} },
                { "title": "10"${isScoringQuiz ? ', "correct": false' : ''} }
              ]
            }
          ]
        }
      `;

      const userPrompt = `
        ${data.message}

        Generate ${data.amountQuestions} questions with answers.

        Focus on correctness and clarity.
        Avoid uncertain or incomplete information.
      `;

      const messages = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ];

      const response: Response = await _http.post(_url, { messages });
      if (!response.ok) return rejectWithValue((await response.json()) as any);

      const result = await response.json();

      if (result?.questions) {
        result.questions = result.questions.slice(0, data.amountQuestions).map((q: any) => ({
          ...q,
          answers: shuffleAnswers(q.answers)
        }));
      }

      return result;
    }
  ),
  generateQuestion: createAsyncThunk(
    'aiChat/Generate question',
    async (questionIndex: number, { rejectWithValue, getState }) => {
      const state = getState() as RootState;
      const { userPrompt, content } = state.aiChat;
      const { quiz } = state.quizzes;
      const isScoringQuiz = quiz && [QuizTypes.Prediction, QuizTypes.Scoring, QuizTypes.BeatTheExpert, QuizTypes.Streak].includes(quiz.type);

      const questions = content?.questions || [];

      const existingTitles = questions.map((q) => `- ${q.title}`).join('\n');

      const messages = [
        {
          role: 'system',
          content: `Create one unique question. Add answers${
            isScoringQuiz
              ? ', should be only one correct answer. Add boolean "correct" field to this answer.'
              : '.'
          } Convert everything to JSON in this model: { title: String, answers: [{ "title": String${
            isScoringQuiz ? ', "correct": Boolean' : ''
          } }, ...]}

          IMPORTANT:
          - Do NOT repeat or create something very similar to any of these existing questions:
          ${existingTitles || '(none)'}`
        },
        { role: 'user', content: userPrompt }
      ];

      const response: Response = await _http.post(_url, { messages });
      if (!response.ok) return rejectWithValue((await response.json()) as any);

      const result = await response.json();

      if (result?.answers) {
        result.answers = shuffleAnswers(result.answers);
      }

      return result;
    }
  ),
  generateTranslations: createAsyncThunk(
    'aiChat/Generate translations',
    async (data:any, { rejectWithValue }) => {
      const { language, translations } = data;

      const messages = [
        { role: 'system', content: `You get a JSON object with the original language field. Save JSON keys and add translations to the language: ${language}. Convert everything to JSON in this model: { ${language}:{ ...translations } }` },
        { role: 'user', content: JSON.stringify(translations) },
      ]
      const response:Response = await _http.post(_url, {messages});
      if ( !response.ok ) return rejectWithValue((await response.json()) as any);
      return response.json();
    }
  ),
}

export default AiChatAsync;

function shuffleAnswers<T extends { title: string; correct?: boolean }>(answers: T[]): T[] {
  const shuffled = [...answers];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
